import { useEffect, useMemo } from 'react';

import { supabaseClient } from '@auth-v2';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { debounce } from 'lodash';

import { subscribeToLayout } from '@data';
import { queryKeys } from '@queries';
import { userAtom } from '@store';

const DEBOUNCE_MS = 200;

export const useSubscribeToLayout = (layoutId: number) => {
    const user = useAtomValue(userAtom);
    const queryClient = useQueryClient();

    const debouncedInvalidations = useMemo(
        () =>
            debounce((userId: string, layoutId: number) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.layout.all(userId) });
                queryClient.invalidateQueries({
                    queryKey: queryKeys.layoutCustomization.base(userId, layoutId),
                });
            }, DEBOUNCE_MS),
        [queryClient],
    );

    useEffect(() => {
        if (!user?.id || layoutId < 0) return;

        const subscription = subscribeToLayout(supabaseClient, layoutId, () => {
            debouncedInvalidations(user.id, layoutId);
        });
        return () => {
            debouncedInvalidations.cancel();
            subscription.unsubscribe();
        };
    }, [layoutId, queryClient, user?.id, debouncedInvalidations]);
};
